/**
 * useTrustedShareReceipts — reads delivery receipts (sent / failed /
 * skipped) from `trusted_share_outbox` for a specific pre-meet
 * checklist, scoped by RLS to the calling member.
 *
 * Each row is one delivery attempt to one Trusted Circle contact.
 * Rows are append-only on the server: a retry produces a *new* row
 * with the latest outcome rather than mutating the previous one.
 *
 * For UI purposes we expose the rows in chronological order plus a
 * `latestByContact` map (newest receipt per contact_id) so the panel
 * can show the *current* delivery state without manual reduction.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";

export type TrustedShareReceiptStatus = "sent" | "failed" | "skipped" | "pending";

export interface TrustedShareReceipt {
  id: string;
  user_id: string;
  checklist_id: string;
  contact_id: string;
  channel: "email" | "sms";
  recipient_name: string | null;
  recipient_email: string | null;
  recipient_phone: string | null;
  status: TrustedShareReceiptStatus;
  provider_message_id: string | null;
  error_message: string | null;
  created_at: string;
}

export function useTrustedShareReceipts(checklistId: string | null | undefined) {
  const { user } = useAuth();
  const [receipts, setReceipts] = useState<TrustedShareReceipt[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!user || !checklistId) {
      setReceipts([]);
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error } = await (supabase as any)
      .from("trusted_share_outbox")
      .select("*")
      .eq("user_id", user.id)
      .eq("checklist_id", checklistId)
      .order("created_at", { ascending: true });
    if (error) {
      setError(error.message);
      setReceipts([]);
    } else {
      setReceipts((data ?? []) as TrustedShareReceipt[]);
    }
    setLoading(false);
  }, [user, checklistId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const latestByContact = useMemo(() => {
    const map = new Map<string, TrustedShareReceipt>();
    for (const r of receipts) {
      // receipts are sorted ascending — last write wins
      map.set(r.contact_id, r);
    }
    return map;
  }, [receipts]);

  const counts = useMemo(() => {
    let sent = 0;
    let failed = 0;
    let skipped = 0;
    for (const r of latestByContact.values()) {
      if (r.status === "sent") sent += 1;
      else if (r.status === "failed") failed += 1;
      else if (r.status === "skipped") skipped += 1;
    }
    return { sent, failed, skipped, total: latestByContact.size };
  }, [latestByContact]);

  return { receipts, latestByContact, counts, loading, error, refresh };
}
